/**
 * Integracoes de uma empresa, SOMENTE LEITURA (aba do detalhe).
 *
 * Decisoes nao obvias:
 *  - Os provedores que o servidor lista (hoje os oito do motor: Kiwify,
 *    Hotmart, Shopify, o GENERICO -- checkout proprio ou qualquer plataforma
 *    sem adaptador, pelo contrato canonico -- e a onda 1: Hubla, Eduzz, Cakto,
 *    Greenn). A previa simulada continua com os quatro de antes.
 *  - Provedor nao conectado continua mostrando a URL: e justamente o que a
 *    equipe precisa copiar para conectar. O que muda e o contorno tracejado e
 *    o titulo apagado -- nao a opacidade do cartao inteiro, que derrubaria o
 *    contraste do texto que ela precisa ler.
 *  - O segredo nunca aparece: so o estado (configurado / faltando) e o aviso
 *    do 401 silencioso quando falta.
 *  - "Ultimo evento" por provedor: integracao que parou de mandar evento e o
 *    defeito mais caro e mais silencioso (nada quebra, so para de entrar
 *    lead). A faixa do topo resume tudo em uma linha.
 *  - O WhatsApp conectado costuma ser o numero do COMERCIAL (ADR-001 E5): quem
 *    responde a um toque cai com a vendedora, e a comissao continua do
 *    Converte Facil. O cartao diz isso.
 */

import { Badge, Icon, Panel, SectionCard } from '../../../components/ui';
import { formatCount, formatDateTime, maskPhoneDisplay } from '../../../lib/format';
import { descreverDias, diasDesde } from '../comparar';
import { agoraDaTela } from '../saude';
import type { CompanyIntegrations } from '../types';
import { PROVEDOR_INFO } from '../vocabulario';
import { CampoCopiavel } from './CampoCopiavel';

function Rotulo({ children }: { readonly children: string }): JSX.Element {
  return <dt className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">{children}</dt>;
}

function haQuanto(iso: string): string {
  const agora = agoraDaTela();
  const minutos = Math.max(0, Math.round((agora.getTime() - new Date(iso).getTime()) / 60_000));
  if (minutos < 60) {
    return `há ${minutos} min`;
  }
  if (minutos < 60 * 24) {
    return `há ${Math.round(minutos / 60)} h`;
  }
  return `há ${descreverDias(diasDesde(iso, agora.toISOString()))}`;
}

export function PainelIntegracoes({ integrations }: { readonly integrations: CompanyIntegrations }): JSX.Element {
  const { webhooks, whatsapp, voice } = integrations;
  const conectados = webhooks.filter((w) => w.connected);
  const semSegredo = conectados.filter((w) => w.secretState === 'missing');
  const ultimos = conectados.map((w) => w.lastEventAt).filter((v): v is string => v !== null);
  const ultimo = ultimos.reduce<string | null>((max, v) => (max === null || v > max ? v : max), null);

  return (
    <div className="flex flex-col gap-4">
      <Panel tone={semSegredo.length > 0 || conectados.length === 0 ? 'warning' : 'surface'} className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3 text-sm">
        <span className="flex items-center gap-2 text-ink">
          <Icon name="link" size="sm" className="text-brand" />
          <strong className="tabular-nums">{formatCount(conectados.length)}</strong> de {formatCount(webhooks.length)} provedores conectados
        </span>
        <span className="flex items-center gap-2 text-ink-muted">
          <Icon name="chave" size="sm" className={semSegredo.length > 0 ? 'text-status-warning' : 'text-status-good'} />
          {semSegredo.length === 0 ? 'Todos com segredo' : `${formatCount(semSegredo.length)} sem segredo (eventos recusados com 401)`}
        </span>
        <span className="flex items-center gap-2 text-ink-muted">
          <Icon name="raio" size="sm" className="text-accent" />
          {ultimo === null ? 'Nenhum webhook recebido ainda' : `Último webhook ${haQuanto(ultimo)} (${formatDateTime(ultimo)})`}
        </span>
      </Panel>

      <SectionCard
        id="integracoes-webhooks"
        title="Webhooks dos provedores"
        icon="link"
        description="Cada provedor envia os eventos de carrinho, pagamento e estorno para a URL da empresa. Sem o segredo, o motor responde 401 e nada entra na régua."
      >
        <ul className="grid gap-4 lg:grid-cols-2">
          {webhooks.map((w) => {
            const info = PROVEDOR_INFO[w.provider];
            const semChave = w.connected && w.secretState === 'missing';
            return (
              <li key={w.provider}>
                <Panel tone={w.connected ? 'surface' : 'elevated'} className={`flex h-full flex-col gap-3 p-4 ${w.connected ? '' : 'border-dashed border-edge'}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h3 className={`text-base font-semibold ${w.connected ? 'text-ink' : 'text-ink-muted'}`}>{info.label}</h3>
                      <p className="text-xs text-ink-subtle">{info.descricao}</p>
                    </div>
                    <Badge tone={w.connected ? 'good' : 'neutral'} dot>
                      {w.connected ? 'Conectado' : 'Não conectado'}
                    </Badge>
                  </div>
                  <CampoCopiavel label="URL do webhook" value={w.webhookUrl} hint={info.hint} />
                  <dl className="grid grid-cols-2 gap-3">
                    <div>
                      <Rotulo>Segredo de assinatura</Rotulo>
                      <dd className="mt-1">
                        {w.secretState === 'configured' ? (
                          <Badge tone="good" icon="chave">
                            Configurado
                          </Badge>
                        ) : (
                          <Badge tone={w.connected ? 'warning' : 'neutral'} icon="chave">
                            Não configurado
                          </Badge>
                        )}
                      </dd>
                    </div>
                    <div>
                      <Rotulo>Último evento</Rotulo>
                      <dd className="mt-1 text-sm text-ink" title={w.lastEventAt === null ? undefined : formatDateTime(w.lastEventAt)}>
                        {w.lastEventAt === null ? <span className="text-ink-subtle">{w.connected ? 'Nenhum ainda' : '—'}</span> : haQuanto(w.lastEventAt)}
                      </dd>
                    </div>
                  </dl>
                  {semChave ? (
                    <p className="flex items-start gap-2 text-xs text-ink-muted">
                      <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
                      <span>Provedor conectado sem segredo: todo evento dele está sendo recusado com 401. Configure o segredo antes de esperar leads.</span>
                    </p>
                  ) : null}
                  {w.provider === 'generic' ? <p className="text-xs text-ink-subtle">{info.segredoHint} Contrato completo em docs/INTEGRACOES-WEBHOOK-GENERICO.md.</p> : null}
                </Panel>
              </li>
            );
          })}
        </ul>
      </SectionCard>

      <div className="grid gap-4 md:grid-cols-2">
        <SectionCard id="integracoes-whatsapp" title="WhatsApp (Evolution API)" icon="whatsapp" description="Instância que envia os toques de WhatsApp da régua.">
          <CampoCopiavel label="Instância" value={whatsapp.instanceName} />
          <dl className="mt-4 flex flex-col gap-3 text-sm">
            <div>
              <Rotulo>Número conectado</Rotulo>
              <dd className="mt-1 tabular-nums text-ink">{maskPhoneDisplay(whatsapp.phoneE164)}</dd>
            </div>
            <div>
              <Rotulo>Estado</Rotulo>
              <dd className="mt-1">
                <Badge tone={whatsapp.state === 'connected' ? 'good' : 'critical'} dot>
                  {whatsapp.state === 'connected' ? 'Sessão ativa' : 'Desconectado: ler o QR code de novo'}
                </Badge>
              </dd>
            </div>
          </dl>
          <p className="mt-4 flex items-start gap-2 text-xs text-ink-subtle">
            <Icon name="usuarios" size="xs" className="mt-0.5 shrink-0" />
            <span>
              Costuma ser o número do comercial: quem responde a um toque cai com a vendedora. A venda fechada por ali continua creditada ao último toque do motor; “fechado por” é
              informação adicional.
            </span>
          </p>
        </SectionCard>

        <SectionCard id="integracoes-voz" title="Voz (Vapi)" icon="robo" description="Assistente de voz que liga 30 minutos depois do abandono, até 2 tentativas.">
          <CampoCopiavel label="ID do assistente" value={voice.assistantId} hint={`Assistente: ${voice.assistantName}`} />
          <dl className="mt-4 flex flex-col gap-3 text-sm">
            <div>
              <Rotulo>Número de origem</Rotulo>
              <dd className="mt-1 tabular-nums text-ink">{maskPhoneDisplay(voice.originPhoneE164)}</dd>
            </div>
            <div>
              <Rotulo>Estado</Rotulo>
              <dd className="mt-1">
                <Badge tone={voice.state === 'ready' ? 'good' : 'warning'} dot>
                  {voice.state === 'ready' ? 'Pronto para ligar' : 'Aguardando configuração'}
                </Badge>
              </dd>
            </div>
          </dl>
        </SectionCard>
      </div>
    </div>
  );
}
